<template>
  <div class="animated fadeIn kb-page">

    <!-- HEADER -->
    <div class="pk-page-header">
      <div class="pk-page-header__left">
        <div class="pk-page-header__icon pk-page-header__icon--teal">
          <i class="fas fa-database"></i>
        </div>
        <div>
          <h2 class="pk-page-header__title">{{ $t('kortex.knowledgeBases') }}</h2>
          <p class="pk-page-header__sub" v-if="knowledgeBases.length > 0">{{ knowledgeBases.length }} total</p>
        </div>
      </div>
      <button v-if="canCreateKBs || loading" class="pk-btn-primary" @click="showCreateDialog = true">
        <i class="fas fa-plus"></i> {{ $t('kortex.createKnowledgeBase') }}
      </button>
    </div>

    <!-- Upgrade gate -->
    <div v-if="!loading && !canCreateKBs" class="pk-upgrade-gate">
      <div class="pk-upgrade-gate__icon"><i class="fas fa-lock"></i></div>
      <h3 class="pk-upgrade-gate__title">{{ $t('kortex.upgradeToUseKBs') }}</h3>
      <p class="pk-upgrade-gate__desc">{{ $t('kortex.upgradeToUseKBsDesc') }}</p>
      <router-link :to="{ name: 'marketplace.List', query: { open: 'protokol-kortex' } }" class="pk-upgrade-gate__btn">
        <i class="fas fa-arrow-up"></i> {{ $t('kortex.upgradePlan') }}
      </router-link>
    </div>

    <!-- CARD -->
    <div v-if="loading || canCreateKBs" class="pk-card">
      <pk-data-table
        :columns="columns"
        :rows="knowledgeBases"
        :loading="loading"
        row-key="uuid"
        empty-icon="fas fa-database"
        :empty-text="$t('kortex.noKnowledgeBases')"
      >
        <template #cell(name)="{ row }">
          <router-link
            class="pk-name-link"
            :to="{ name: 'CortexKnowledgeBaseDetail', params: { uuid: row.uuid } }"
          >
            {{ row.name }}
          </router-link>
          <div v-if="row.description" class="pk-sub-text">{{ row.description }}</div>
        </template>

        <template #cell(document_count)="{ row }">
          <span class="pk-count-badge pk-count-badge--blue">{{ row.document_count || 0 }}</span>
        </template>

        <template #cell(chunk_count)="{ row }">
          <span class="pk-count-badge pk-count-badge--gray">{{ row.chunk_count || 0 }}</span>
        </template>

        <template #cell(created_at)="{ row }">
          <span class="pk-date">{{ formatDate(row.created_at) }}</span>
        </template>

        <template #cell(actions)="{ row }">
          <button class="action-btn action-btn--danger" @click="confirmDelete(row)" title="Delete">
            <i class="fas fa-trash"></i>
          </button>
        </template>
      </pk-data-table>
    </div>

    <!-- Create Knowledge Base dialog -->
    <main-dialog
      :show="showCreateDialog"
      :title="$t('kortex.createKnowledgeBase')"
      @close="closeCreateDialog"
    >
      <b-form @submit.prevent="create">
        <b-form-group :label="$t('kortex.name')" label-for="kb-name">
          <b-form-input
            id="kb-name"
            v-model="createForm.name"
            :placeholder="$t('kortex.namePlaceholder')"
            required
          ></b-form-input>
        </b-form-group>
        <b-form-group :label="$t('kortex.description')" label-for="kb-desc">
          <b-form-textarea
            id="kb-desc"
            v-model="createForm.description"
            :placeholder="$t('kortex.descriptionPlaceholder')"
            rows="3"
          ></b-form-textarea>
        </b-form-group>
      </b-form>
      <template #footer>
        <b-button variant="secondary" @click="closeCreateDialog">
          {{ $t('cancel') }}
        </b-button>
        <b-button variant="primary" :disabled="creating" @click="create">
          <b-spinner v-if="creating" small class="mr-1"></b-spinner>
          {{ $t('create') }}
        </b-button>
      </template>
    </main-dialog>

    <!-- Delete confirmation dialog -->
    <main-dialog
      :show="showDeleteDialog"
      :title="$t('kortex.deleteKnowledgeBase')"
      @close="showDeleteDialog = false"
    >
      <p>{{ $t('kortex.confirmDeleteKb') }}</p>
      <template #footer>
        <b-button variant="secondary" @click="showDeleteDialog = false">
          {{ $t('cancel') }}
        </b-button>
        <b-button variant="danger" :disabled="deleting" @click="doDelete">
          <b-spinner v-if="deleting" small class="mr-1"></b-spinner>
          {{ $t('delete') }}
        </b-button>
      </template>
    </main-dialog>

  </div>
</template>

<script>
export default {
  name: 'KnowledgeBases',
  data: function () {
    return {
      loading: false,
      creating: false,
      deleting: false,
      showCreateDialog: false,
      showDeleteDialog: false,
      pendingDelete: null,
      createForm: {
        name: '',
        description: ''
      }
    }
  },
  computed: {
    canCreateKBs: function () {
      return this.$store.getters['cortex/canCreateKBs']
    },
    knowledgeBases: function () {
      return this.$store.state.cortex.knowledgeBases
    },
    columns: function () {
      return [
        { key: 'name', label: this.$t('kortex.name') },
        { key: 'document_count', label: this.$t('kortex.documents') },
        { key: 'chunk_count', label: this.$t('kortex.chunks') },
        { key: 'created_at', label: this.$t('createdAt') },
        { key: 'actions', label: '', tdClass: 'pk-td-actions' }
      ]
    }
  },
  mounted: function () {
    this.load()
  },
  methods: {
    load: function () {
      var self = this
      self.loading = true
      Promise.all([
        self.$store.dispatch('cortex/loadTier'),
        self.$store.dispatch('cortex/loadKnowledgeBases')
      ]).finally(function () {
        self.loading = false
      })
    },
    create: function () {
      if (!this.createForm.name.trim()) return
      this.creating = true
      this.$store.dispatch('cortex/createKnowledgeBase', {
        name: this.createForm.name,
        description: this.createForm.description
      }).then(function () {
        this.closeCreateDialog()
      }.bind(this)).finally(function () {
        this.creating = false
      }.bind(this))
    },
    confirmDelete: function (kb) {
      this.pendingDelete = kb
      this.showDeleteDialog = true
    },
    doDelete: function () {
      if (!this.pendingDelete) return
      this.deleting = true
      this.$store.dispatch('cortex/deleteKnowledgeBase', this.pendingDelete.uuid).then(function () {
        this.showDeleteDialog = false
        this.pendingDelete = null
      }.bind(this)).finally(function () {
        this.deleting = false
      }.bind(this))
    },
    closeCreateDialog: function () {
      this.showCreateDialog = false
      this.createForm = { name: '', description: '' }
    },
    formatDate: function (dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.kb-page {
  padding: 8px 0;
}

/* Upgrade gate */
.pk-upgrade-gate {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}
.pk-upgrade-gate__icon {
  font-size: 48px;
  color: rgba(255,255,255,0.15);
  margin-bottom: 20px;
}
.pk-upgrade-gate__title {
  font-size: 20px;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
  margin-bottom: 10px;
}
.pk-upgrade-gate__desc {
  font-size: 14px;
  color: rgba(255,255,255,0.45);
  max-width: 420px;
  line-height: 1.6;
  margin-bottom: 28px;
}
.pk-upgrade-gate__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: 8px;
  background: #6c5dd3;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s;
}
.pk-upgrade-gate__btn:hover {
  background: #7d6fe0;
  color: #fff;
  text-decoration: none;
}
</style>
